import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import ProgramsDirectory from "@/components/opportunities/ProgramsDirectory";
import RecommendedForYou from "@/components/opportunities/RecommendedForYou";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState } from "react";
import { useSiteContent } from "@/hooks/useSiteContent";
import { getBlock } from "@/lib/siteContent";
import { useCoachReadiness } from "@/hooks/useCoachReadiness";
import { Sparkles } from "lucide-react";
import RequestQuoteDialog from "@/components/affiliates/RequestQuoteDialog";

const EMRKT_AFFILIATE_ID = "6ce2f5fc-4791-4f80-8c70-a756f6b60751";

const Opportunities = () => {
  const { blocks: copy } = useSiteContent("opportunities");
  const heroHeadline = getBlock(copy, "hero_headline", "Capital, coaching & contracts that move Alabama forward");
  const pageIntro = getBlock(
    copy,
    "page_intro",
    "A verified directory of grants, loans, mentorship, technical assistance, and contracting programs from the SBA and partner agencies — aligned to our five focus areas. Save what fits, mark what you've applied to, and track everything from your profile.",
  );
  const { user } = useAuth();
  const { hasAssessment, focusAreas } = useCoachReadiness();
  const [assistOpen, setAssistOpen] = useState(false);

  useEffect(() => {
    document.title = "Funding Opportunities | emrkt.co";
    const desc = "Verified directory of grants and funding for Alabama communities, with Coach-recommended matches.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="pt-24 pb-16">
        <section className="container mx-auto px-6 max-w-5xl text-center mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Funding Opportunities</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-4">
            {heroHeadline}
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            {pageIntro}
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            {user ? (
              <Link to="/profile?tab=saved">
                <Button variant="outline" size="sm">View saved opportunities</Button>
              </Link>
            ) : (
              <Link to="/auth">
                <Button variant="outline" size="sm">Sign in to save</Button>
              </Link>
            )}
            <Link to="/coach">
              <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                {hasAssessment ? "Open your Coach" : "Get a readiness assessment"}
              </Button>
            </Link>
          </div>
        </section>
        <section className="container mx-auto px-6 max-w-6xl space-y-12">
          <Tabs defaultValue="programs" className="w-full">
            <TabsList>
              <TabsTrigger value="programs">Program Directory</TabsTrigger>
              <TabsTrigger value="recommended">Recommended</TabsTrigger>
            </TabsList>
            <TabsContent value="programs" className="mt-6">
              <div className="mb-4">
                <h2 className="font-display text-2xl font-bold text-foreground">Program Directory</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Browse every verified grant program in our Alabama directory.
                </p>
              </div>
              <ProgramsDirectory />
            </TabsContent>
            <TabsContent value="recommended" className="mt-6">
              <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
                <div>
                  <h2 className="font-display text-2xl font-bold text-foreground flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" /> Recommended for you
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Verified programs aligned to your Coach assessment.
                  </p>
                </div>
              </div>
              <RecommendedForYou />
            </TabsContent>
          </Tabs>

          {/* Research callout — funders moved to their own page */}
          <div className="border border-border rounded-xl p-5 md:p-6 bg-muted/20 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
            <div className="flex-1">
              <h3 className="font-display text-lg font-semibold">
                Researching who funds work like yours?
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Every Alabama-HQ foundation, sourced from public IRS Form 990 filings — searchable and sortable.
              </p>
            </div>
            <Link
              to="/funders"
              className="inline-flex items-center justify-center gap-2 border border-border px-4 py-2 rounded-full text-sm font-semibold hover:border-primary/40 transition-colors shrink-0"
            >
              Explore funders
            </Link>
          </div>

          {/* CTA — Grant Application Assistance */}
          <div className="border-t border-border pt-12">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">
                Need help applying for grants?
              </h2>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
                The Emerging Market helps Alabama organizations find, qualify for, and win the grants
                and contracts designed for the work they already do. From pipeline development to
                submission support, we sit between the funder and your organization until the dollars move.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Button size="lg" onClick={() => setAssistOpen(true)}>
                  Get Application Assistance
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/services">See our services</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <RequestQuoteDialog
        open={assistOpen}
        onOpenChange={setAssistOpen}
        affiliateId={EMRKT_AFFILIATE_ID}
        affiliateName="The Emerging Market"
      />
    </div>
  );
};

export default Opportunities;